// Lib
import { isEmpty, isString, curry, escape as lodashEscape } from 'lodash/fp';

// Utils
import { prop, propIn } from '../../utils/immutableHelper';
import { hasScope } from '../../utils/googleUtils';
import { getTimestamp } from '../../utils/timeUtil';

// Constants
import { USER_AUTHENTICATION_METHODS, USER_ROLES } from '../userConstants';
import { DEFAULT_CONTENT_LIMIT, DEFAULT_MAX_REFERRAL_BONUS } from '../contentLimit';

const MAX_NAME_LENGTH = 50;

// All
export const getGoogleScope = propIn(['google', 'scope']);

export const getUserEmail = prop('email');
export const getRootBoardId = prop('rootBoardId');
export const getContentLimitObject = prop('contentLimit');
export const getCurrentContentLimit = propIn(['contentLimit', 'current']);
export const getDefaultContentLimit = (user) => propIn(['contentLimit', 'default'], user) || DEFAULT_CONTENT_LIMIT;
export const getGiftBonus = (user) => propIn(['contentLimit', 'giftBonus'], user) || 0;
export const getReferralBonus = (user) => propIn(['contentLimit', 'referralBonus'], user);
export const getMaxReferralBonus = (user) =>
    propIn(['contentLimit', 'maxReferralBonus'], user) || DEFAULT_MAX_REFERRAL_BONUS;
export const getLastCheckedNotifications = (user) => prop('lastCheckedNotifications', user) || 0;
export const getFlags = prop('flags');
export const getFeatureFlag = curry((flagName, user) => propIn(['flags', flagName], user) || false);
export const getUserTimezone = prop('timezone');
export const getEnabledExperiments = prop('experiments');

/**
 * @param {object} user - The user object to read the segment from.
 * @param {string|null} [defaultValue='generic'] - Fallback value when no segment is found. Can be null.
 * @returns {string|null} The resolved user segment or the provided default (which may be null).
 */
export const getUserSegment = (user, defaultValue = 'generic') =>
    propIn(['userSegment', 'segment'], user) || propIn(['segments', 0], user) || defaultValue;

// Server side only
export const getUserLocalPassword = propIn(['local', 'password']);
export const getGoogleData = prop('google');
export const getGoogleId = propIn(['google', 'id']);
export const getAppleId = propIn(['apple', 'id']);
export const getAccessToken = propIn(['google', 'accessToken']);
export const getRefreshToken = propIn(['google', 'refreshToken']);

// Property getters
export const getUserId = prop('_id');
export const getStatus = prop('status');
export const getStatusDetails = prop('statusDetails');
export const getUserImage = prop('image');
export const getUserEducation = prop('education');
export const getUserOnboardingStep = prop('onboarding');
export const getEmailIsVerified = (user) => !!prop('emailVerifiedDate', user);

// Subscriptions
export const getSubscriptionId = prop('subscriptionId');
export const getSubscriptionInviteId = propIn(['subscriptionMeta', 'inviteId']);
export const getSubscriptionRequiredDomains = propIn(['subscriptionMeta', 'requiredDomains']);
export const getSubscription = prop('subscription');
export const getCustomerId = prop('customerId');

// Settings
export const getUserSettings = prop('settings');
export const getPushNotificationsPreference = propIn(['settings', 'pushNotifications']);
export const getEmailNotificationsPreference = propIn(['settings', 'email']);
export const getUserShowGrid = propIn(['settings', 'showGrid']);
export const getUserPushCards = propIn(['settings', 'pushCards']);
export const getUserEnableTemplatePicker = propIn(['settings', 'enableTemplatePicker']);
export const getUserEnableDrawingInkEffect = propIn(['settings', 'enableDrawingInkEffect']);
export const getUserEnableDoubleClickToCreateNotes = propIn(['settings', 'enableDoubleClickToCreateNotes']);
export const getUserEnableUndoRedoButtonsForWeb = propIn(['settings', 'enableUndoRedoButtons']);
export const getUserEnableUndoRedoButtonsForIpad = propIn(['settings', 'enableUndoRedoButtonsForIpad']);
export const getUserEnableHaptics = propIn(['settings', 'enableHaptics']);
export const getUserLanguagePreferenceForTables = propIn(['settings', 'languagePreference']);
export const getUserCurrencyPreference = propIn(['settings', 'currencyPreference']);
// cant use language preference as it is already mapped to tables and date formats
export const getUserLocalePreference = propIn(['settings', 'localePreference']);
export const getUserTimeFormatPreference = propIn(['settings', 'timeFormatPreference']);
export const getUserCurrencyDisplayLocale = (user, browserLocale) =>
    getUserLanguagePreferenceForTables(user) || getUserLocalePreference(user) || browserLocale;

export const getEnableAnonymousViewing = (user) => !!propIn(['settings', 'enableAnonymousViewing'], user);
export const getUserDisableSerifHeadings = propIn(['settings', 'disableSerifHeadings']);
export const getUserPreferredColorDisplay = propIn(['settings', 'preferredColorDisplay']);
export const getUserEnableFeatureSuggestion = propIn(['settings', 'enableFeatureSuggestion']);

export const getUserPrimaryToolsSettings = propIn(['settings', 'primaryTools']);
export const getUserPrimaryToolsTouchSettings = propIn(['settings', 'primaryToolsTouch']);

export const getUserReactionsRecentlyUsed = propIn(['settings', 'reactionsRecent']);
export const getUserReactionsSkinTone = propIn(['settings', 'reactionsSkinTone']);

export const getAccountCreationDate = prop('accountCreationDate');
export const getAccountRegistrationDate = prop('accountRegistrationDate');

export const getAccountAge = (user) => {
    if (!getAccountRegistrationDate(user)) return -1;
    return getTimestamp() - getAccountRegistrationDate(user);
};

export const getUserBrowserExtensionsSettings = (currentUser) => propIn(['settings', 'browserExtensions'], currentUser);

// Client side only
export const getIsGuestProp = prop('isGuest');
export const getIsAnonymousProp = prop('isAnonymous');
export const getReferralCode = prop('referrerCode');
export const getUserCurrentBoardId = prop('currentBoardId');
export const getAuthMethods = prop('authMethods');
export const getShareCount = (user) => prop('shareCount', user) || 0;
export const shareCountComparator = (userA, userB) => getShareCount(userB) - getShareCount(userA);

export const hasPasswordAuthenticationMethod = (user) => {
    const authMethods = getAuthMethods(user);
    return authMethods && authMethods.includes(USER_AUTHENTICATION_METHODS.PASSWORD);
};

export const hasLinkedGoogleData = (user) => !isEmpty(getGoogleData(user));
export const hasGoogleScope = (user, scope) => hasLinkedGoogleData(user) && hasScope(getGoogleScope(user), scope);

// Functions
export const hasImage = (user) => !!getUserImage(user);

export const getRole = (user) => (isString(user) ? user : prop('role', user));

export const isAdmin = (user) => getRole(user) === USER_ROLES.ADMIN;
export const isStaff = (user) => getRole(user) === USER_ROLES.STAFF || isAdmin(user);
export const isExampleRole = (user) => getRole(user) === USER_ROLES.EXAMPLE;

/**
 *
 * @param {ExperimentId} feature
 * @param {MNUser} user
 * @returns {boolean}
 */
export const isFeatureEnabled = (feature, user) => !!getEnabledExperiments(user)?.includes(feature);

const sanitizeNameValue = (value) => {
    if (!isString(value)) return '';

    return value.slice(0, MAX_NAME_LENGTH);
};

const buildDisplayName = ({ givenName = '', familyName = '' }) => [givenName, familyName].filter(Boolean).join(' ');

/**
 * Escapes and trims one name value, matching what the user model's pre-save hook applies.
 */
export const sanitizeUserNameValue = (value = '') => lodashEscape(value).trim();

export const buildUserName = ({ givenName = '', familyName = '' }) => {
    const sanitizedGivenName = sanitizeNameValue(givenName);
    const sanitizedFamilyName = sanitizeNameValue(familyName);

    return {
        givenName: sanitizedGivenName,
        familyName: sanitizedFamilyName,
        displayName: buildDisplayName({
            givenName: sanitizedGivenName,
            familyName: sanitizedFamilyName,
        }),
    };
};
